import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { DriverForm, type DriverFormData } from './DriverForm';
import type { Team, Driver, Vehicle, UpdateDriverRequest } from '@flote/contracts/http';

interface DriverFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: DriverFormData | UpdateDriverRequest) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  vehicles: Vehicle[];
  driver?: Driver;
  driverTeamIds?: string[];
}

export const DriverFormModal = memo<DriverFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  teams,
  vehicles,
  driver,
  driverTeamIds,
}) => {
  const isEditMode = !!driver;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  const initialData = driver
    ? {
        name: driver.name,
        phone: driver.phone,
        displayName: driver.displayName,
        teams: driverTeamIds ?? [],
        vehicleId: driver.vehicleId,
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>{isEditMode ? 'Edit Driver' : 'Create Driver'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update driver information and settings'
              : 'Add a new driver to your delivery team'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <DriverForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              teams={teams}
              vehicles={vehicles}
              initialData={initialData}
              mode={isEditMode ? 'edit' : 'create'}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

DriverFormModal.displayName = 'DriverFormModal';
